import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  MessageSquare,
  FileText,
  Users,
  Wallet,
  TrendingUp,
  Settings,
  LogOut,
  Clock,
  CheckCircle2,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { GlassCard, Eyebrow, StatBlock } from "../components/Section";

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [walletApplications, setWalletApplications] = useState([]);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, walletRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/justice-wallet/my-applications`, { withCredentials: true }).catch(() => ({ data: { applications: [] } })),
      ]);
      setStats(statsRes.data);
      setWalletApplications(walletRes.data.applications);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    }
  };

  const citizenCards = [
    { icon: MessageSquare, title: "Justice-AI Assistant", desc: "Free preliminary legal guidance — 24/7.", href: "/ai-assistant" },
    { icon: FileText, title: "My Requests & Cases", desc: "Track your submitted matters and lawyer assignments.", href: "/my-requests" },
    { icon: Users, title: "Find a Lawyer", desc: "Match with verified legal professionals.", href: "/get-legal-help" },
    { icon: Wallet, title: "Justice Wallet", desc: "Apply for funded legal assistance.", href: "/for-ngos" },
  ];
  const lawyerCards = [
    { icon: Users, title: "Client Requests Inbox", desc: "Review and accept matched matters.", href: "/lawyer/inbox" },
    { icon: TrendingUp, title: "AI Copilot", desc: "Research, drafting, case strategy.", href: "/ai-assistant" },
    { icon: Wallet, title: "Pilot Subscription", desc: "Activate your free pilot — card on file, no charge.", href: "/lawyer-subscribe" },
    { icon: Settings, title: "Profile & Verification", desc: "NBA status, practice areas, calendar.", href: "#" },
  ];
  const adminCards = [
    { icon: Settings, title: "Admin Console", desc: "Sign-ups, contacts, lawyers, cases.", href: "/admin" },
    { icon: Users, title: "Lawyer Inbox", desc: "View all client requests across the platform.", href: "/lawyer/inbox" },
    { icon: MessageSquare, title: "Justice-AI Assistant", desc: "Test the AI flow as a citizen.", href: "/ai-assistant" },
    { icon: TrendingUp, title: "Platform Metrics", desc: "Live overview of sign-ups and cases.", href: "/admin" },
  ];

  const quickActions = user?.role === "admin" ? adminCards : user?.role === "lawyer" ? lawyerCards : citizenCards;

  return (
    <main className="flex-1 bg-ink text-white min-h-[calc(100vh-5rem)] py-10" data-testid="dashboard-page">
      <div className="container mx-auto px-5 sm:px-8 max-w-7xl">
        {/* Welcome */}
        <div className="mb-10">
          <Eyebrow>Your Dashboard</Eyebrow>
          <h1 className="font-playfair text-3xl md:text-5xl text-white mt-3 leading-tight">
            Welcome back,{" "}
            <span className="italic text-aj-gradient">{user?.name?.split(" ")[0] || "there"}.</span>
          </h1>
          <p className="font-inter text-base text-white/55 mt-2">
            {user?.role === "lawyer" ? "Manage your practice and connect with verified clients." : "Access free legal help, resources and the Justice Wallet."}
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            {[
              { v: stats.users_helped?.toLocaleString() || "0", l: "Users Helped" },
              { v: stats.legal_questions_answered?.toLocaleString() || "0", l: "Questions Answered" },
              { v: stats.templates_available || "0", l: "Templates Available" },
              { v: stats.verified_lawyers || "0", l: "Verified Lawyers" },
            ].map((s, i) => (
              <GlassCard key={i} className="!p-6" data-testid={`dashboard-stat-${i}`}>
                <StatBlock value={s.v} label={s.l} />
              </GlassCard>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quick actions */}
          <div className="lg:col-span-2">
            <h2 className="font-playfair text-2xl text-white mb-4">Quick Actions</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {quickActions.map((c, i) => (
                <Link key={i} to={c.href} data-testid={`quick-action-${i}`}>
                  <GlassCard glow className="h-full group">
                    <div className="w-11 h-11 rounded-xl bg-turquoise/10 border border-turquoise/30 flex items-center justify-center mb-4">
                      <c.icon className="w-5 h-5 text-turquoise" />
                    </div>
                    <h3 className="font-playfair text-xl text-white mb-2 flex items-center justify-between">
                      {c.title}
                      <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-turquoise group-hover:translate-x-1 transition-all" />
                    </h3>
                    <p className="font-inter text-sm text-white/60 leading-relaxed">{c.desc}</p>
                  </GlassCard>
                </Link>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            {/* Profile card */}
            <GlassCard data-testid="profile-card">
              <Eyebrow className="mb-4">Your Profile</Eyebrow>
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-full bg-turquoise text-ink flex items-center justify-center font-playfair text-2xl">
                  {user?.name?.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0">
                  <div className="font-playfair text-lg text-white truncate">{user?.name}</div>
                  <div className="font-inter text-xs text-white/50 truncate">{user?.email}</div>
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-turquoise/10 border border-turquoise/30 text-turquoise font-montserrat text-[9px] uppercase tracking-wider mt-1.5">
                    {user?.role}
                  </span>
                </div>
              </div>
              <button
                onClick={logout}
                data-testid="dashboard-logout-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-red-400/30 text-red-300 font-montserrat text-xs uppercase tracking-wider font-semibold hover:bg-red-400/10 transition-all"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign Out
              </button>
            </GlassCard>

            {/* Wallet apps */}
            {walletApplications.length > 0 && (
              <GlassCard data-testid="wallet-applications-card">
                <Eyebrow className="mb-3 flex items-center gap-2"><Wallet className="w-3 h-3" /> Wallet Applications</Eyebrow>
                <div className="space-y-2.5">
                  {walletApplications.slice(0, 3).map((app, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.025] border border-white/8">
                      <div className="min-w-0">
                        <p className="font-inter text-sm text-white/85 truncate max-w-[160px]">
                          {app.case_description?.substring(0, 30)}…
                        </p>
                        <p className="font-inter text-[10px] text-white/40">
                          {new Date(app.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-montserrat text-[9px] uppercase tracking-wider ${
                        app.status === "pending"
                          ? "bg-gold-400/10 text-gold-200 border border-gold-400/30"
                          : app.status === "approved"
                          ? "bg-turquoise/10 text-turquoise border border-turquoise/30"
                          : "bg-white/5 text-white/50 border border-white/10"
                      }`}>
                        {app.status === "pending" ? <Clock className="w-2.5 h-2.5" /> : <CheckCircle2 className="w-2.5 h-2.5" />}
                        {app.status}
                      </span>
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* CTA card */}
            <div className="relative rounded-2xl p-6 overflow-hidden bg-gradient-to-br from-turquoise/20 via-turquoise/5 to-transparent border border-turquoise/30" data-testid="help-card">
              <Sparkles className="w-7 h-7 text-turquoise mb-3" />
              <h3 className="font-playfair text-xl text-white mb-2">Need legal help right now?</h3>
              <p className="font-inter text-sm text-white/70 leading-relaxed mb-5">
                Justice-AI is available 24/7 with preliminary legal guidance.
              </p>
              <Link to="/ai-assistant" data-testid="help-chat-btn" className="btn-aj-primary !py-3 !text-[11px]">
                <MessageSquare className="w-4 h-4" /> Start Chat
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default Dashboard;
